import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as GreenFilledTickMarkIcon } from "../../asserts/logo/GreenFilledTickMarkIcon.svg";

const FONT = "Lato, sans-serif";

const BODY_SX = {
  fontFamily: FONT,
  fontWeight: 400,
  fontStyle: "normal",
  fontSize: "14px",
  lineHeight: "21px",
  letterSpacing: 0,
  color: "#555555",
  textAlign: "center",
  verticalAlign: "middle",
};

const BODY_EMPHASIS_SX = {
  fontFamily: FONT,
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: "14px",
  lineHeight: "21px",
  letterSpacing: 0,
  color: "#555555",
};

/** Bold digits only — Figma: Generated/Custom/test-case counts are 700, labels stay 400. */
function emphasizeCounts(text) {
  if (!text) return null;
  const nodes = [];
  const re = /(\d+)/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = re.exec(text))) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    nodes.push(
      <Box key={`count-${key++}`} component="span" sx={BODY_EMPHASIS_SX}>
        {match[1]}
      </Box>
    );
    last = match.index + match[1].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

const BTN_TEXT_SX = {
  textTransform: "none",
  fontFamily: FONT,
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  verticalAlign: "middle",
};

const PRIMARY_BG =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const PRIMARY_BG_HOVER =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const PRIMARY_BG_ACTIVE =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const PRIMARY_SHADOW = "0px 1px 2px 0px rgba(0, 0, 0, 0.04)";
const PRIMARY_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";
const CANCEL_BG =
  "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), #F5F5F5";
const CANCEL_SHADOW = "0px 1px 2px 0px rgba(0, 0, 0, 0.08)";

const CANCEL_BTN_SX = {
  ...BTN_TEXT_SX,
  boxSizing: "border-box",
  width: 181,
  minWidth: 181,
  maxWidth: 181,
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  gap: "4px",
  borderRadius: "4px",
  border: "1px solid #BBBBBB",
  borderColor: "#BBBBBB",
  backgroundColor: "#F5F5F5",
  backgroundImage: CANCEL_BG,
  color: "#555555",
  boxShadow: CANCEL_SHADOW,
  whiteSpace: "nowrap",
  justifyContent: "center",
  alignItems: "center",
  "&:hover": {
    border: "1px solid #BBBBBB",
    borderColor: "#BBBBBB",
    backgroundColor: "#F5F5F5",
    backgroundImage: CANCEL_BG,
    color: "#555555",
    boxShadow: CANCEL_SHADOW,
  },
  "&:focus, &:focus-visible": {
    border: "1px solid #BBBBBB",
    borderColor: "#BBBBBB",
    backgroundColor: "#F5F5F5",
    backgroundImage: CANCEL_BG,
    color: "#555555",
    boxShadow: CANCEL_SHADOW,
  },
  "&.Mui-disabled": {
    border: "1px solid #BBBBBB",
    backgroundColor: "#F5F5F5",
    backgroundImage: CANCEL_BG,
    color: "#555555",
    opacity: 0.5,
  },
  "& .MuiTouchRipple-root": { display: "none" },
};

const PRIMARY_BTN_SX = {
  ...BTN_TEXT_SX,
  boxSizing: "border-box",
  width: 181,
  minWidth: 181,
  maxWidth: 181,
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  gap: "8px",
  borderRadius: "4px",
  border: "1px solid transparent",
  color: "#FFFFFF",
  bgcolor: "transparent",
  backgroundColor: "transparent",
  backgroundImage: PRIMARY_BG,
  boxShadow: PRIMARY_SHADOW,
  whiteSpace: "nowrap",
  justifyContent: "center",
  alignItems: "center",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: PRIMARY_BG_HOVER,
    boxShadow: PRIMARY_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
    boxShadow: PRIMARY_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
  },
  "&:active:not(.Mui-disabled)": {
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG_ACTIVE} !important`,
    border: "1px solid transparent",
    boxShadow: PRIMARY_INSET,
  },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    color: "#BBBBBB",
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    boxShadow: CANCEL_SHADOW,
    opacity: 1,
    cursor: "not-allowed",
  },
};

export function sourceBreakdownForCases(cases) {
  const counts = { Generated: 0, Modified: 0, Custom: 0 };
  for (const tc of cases) {
    if (tc.source === "Custom") counts.Custom += 1;
    else if (tc.source === "Modified") counts.Modified += 1;
    else counts.Generated += 1;
  }
  const parts = [];
  if (counts.Generated) parts.push(`${counts.Generated} Generated`);
  if (counts.Modified) parts.push(`${counts.Modified} Modified`);
  if (counts.Custom) parts.push(`${counts.Custom} Custom`);
  return parts.join(" + ");
}

export function formatOtherMetricGroups(metricGroups, currentMetricId) {
  const names = metricGroups
    .filter((g) => g.metricId !== currentMetricId)
    .map((g) => g.metricName)
    .filter(Boolean);
  if (!names.length) return "";
  if (names.length === 1) return `(${names[0]})`;
  if (names.length === 2) return `(${names[0]}, ${names[1]})`;
  return `(${names.slice(0, 2).join(", ")}, etc.)`;
}

/** Figma ModalPrompt — bulk confirm ground truth for a metric group. */
export default function ConfirmGroundTruthDialog({
  open,
  metricName,
  confirmCount,
  sourceSummary,
  otherMetricsLabel,
  onClose,
  onConfirm,
  submitting,
}) {
  const name = (metricName || "").trim() || "this metric";
  const countLabel =
    confirmCount === 1 ? "1 test case" : `${confirmCount} test cases`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "4px",
          boxSizing: "border-box",
          width: { xs: "calc(100% - 32px)", sm: 600 },
          maxWidth: 600,
          height: "auto",
          maxHeight: "calc(100vh - 32px)",
          mx: 2,
          pt: "40px",
          pr: { xs: 3, sm: "64px" },
          pb: "40px",
          pl: { xs: 3, sm: "64px" },
          opacity: 1,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px",
          bgcolor: "#FFFFFF",
          boxShadow:
            "0px 0px 10px 0px rgba(0, 0, 0, 0.24), 0px 10px 10px 0px rgba(33, 34, 38, 0.12)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(52, 64, 84, 0.45)" },
        },
      }}
    >
      <IconButton
        size="small"
        onClick={onClose}
        aria-label="Close"
        disabled={submitting}
        disableRipple
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          color: "#555555",
          p: 0,
          width: 24,
          height: 24,
        }}
      >
        <CloseIcon sx={{ fontSize: 24 }} />
      </IconButton>

      <Box
        sx={{
          position: "relative",
          width: 64,
          height: 64,
          flexShrink: 0,
        }}
        aria-hidden
      >
        <Box
          sx={{
            position: "absolute",
            top: 10,
            left: 9.76,
            width: 42.68,
            height: 42.68,
            "& svg": { width: 42.68, height: 42.68, display: "block" },
          }}
        >
          <GreenFilledTickMarkIcon />
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: FONT,
          fontWeight: 900,
          fontStyle: "normal",
          fontSize: "20px",
          lineHeight: "27px",
          letterSpacing: 0,
          color: "#000000",
          textAlign: "center",
          verticalAlign: "middle",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          maxWidth: 472,
        }}
      >
        Confirm Ground Truth for {name}
      </Typography>

      <Typography
        sx={{
          ...BODY_SX,
          display: "block",
          width: "100%",
          maxWidth: 472,
        }}
      >
        You are about to bulk-approve ground truth for{" "}
        <Box component="span" sx={BODY_EMPHASIS_SX}>
          {name}
        </Box>
        <br />
        <Box component="span" sx={BODY_EMPHASIS_SX}>
          {confirmCount}
        </Box>
        {confirmCount === 1 ? " test case" : " test cases"} will be confirmed
        {sourceSummary ? <> | {emphasizeCounts(sourceSummary)}</> : null}
        <br />
        <br />
        This action will mark all{" "}
        <Box component="span" sx={BODY_EMPHASIS_SX}>
          {countLabel}
        </Box>{" "}
        as{" "}
        <Box component="span" sx={BODY_EMPHASIS_SX}>
          ground-truth confirmed
        </Box>{" "}
        for the
        <br />
        {name} metric.
        {otherMetricsLabel ? (
          <> Other metric groups {otherMetricsLabel} will not be affected and can be reviewed independently.</>
        ) : null}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          alignSelf: "center",
          width: "fit-content",
          maxWidth: "100%",
          pt: "16px",
          gap: "16px",
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          disableElevation
          disableRipple
          onClick={onClose}
          disabled={submitting}
          sx={CANCEL_BTN_SX}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disableRipple
          onClick={onConfirm}
          disabled={submitting || !confirmCount}
          sx={PRIMARY_BTN_SX}
        >
          {submitting ? (
            <CircularProgress size={22} sx={{ color: "#fff" }} />
          ) : (
            "Confirm Ground Truth"
          )}
        </Button>
      </Box>
    </Dialog>
  );
}
